// 共享 UI 组件
import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export function PageHeader({
  title, subtitle, stageBadge, actions,
}: {
  title: string;
  subtitle: string;
  stageBadge?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <div className="page-title">
          <h1>{title}</h1>
          {stageBadge && <span className="stage-badge">{stageBadge}</span>}
        </div>
        <div className="page-subtitle">{subtitle}</div>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function ModuleBanner({ title, stageTag }: { title: string; stageTag: string }) {
  return (
    <div className="module-banner">
      <h3>{title}</h3>
      <span className="stage-tag">{stageTag}</span>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`card ${className}`}>{children}</div>;
}

export function CardHead({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="card-head">
      <div className="card-title">{title}</div>
      {children || (sub && <div className="card-sub">{sub}</div>)}
    </div>
  );
}


export function Sparkline({ data, color }: { data: number[]; color?: 'cinnabar' | 'bamboo' | 'gold' }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 80;
    const y = 24 - ((v - min) / range) * 20 - 2;
    return x.toFixed(1) + ',' + y.toFixed(1);
  }).join(' ');
  return (
    <svg className={'sparkline' + (color ? ' ' + color : '')} viewBox="0 0 80 24" preserveAspectRatio="none">
      <polyline points={pts} />
    </svg>
  );
}


export function CountUp({ end, duration = 900, decimals = 0 }: { end: number; duration?: number; decimals?: number }) {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setVal(end); return; }

    const start = performance.now();
    // easeOutBack: spring with slight overshoot for a premium "alive" feel
    const c = 1.70158;
    const easeOutBack = (t: number) => 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = easeOutBack(t);
      setVal(end * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setVal(end);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [end, duration]);

  const formatted = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();
  return <>{formatted}</>;
}

export function Kpi({ label, value, unit, trend, spark }: { label: string; value: ReactNode; unit?: string; trend?: { type: 'up' | 'down' | 'flat'; text: string }; spark?: number[] }) {
  return (
    <div className="kpi">
      <div className="kpi-shine" />
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">
        {value}
        {unit && <span className="kpi-unit">{unit}</span>}
      </div>
      <div className="kpi-foot">
        {trend && <span className={`kpi-delta ${trend.type}`}>{trend.text}</span>}
        {spark && <Sparkline data={spark} />}
      </div>
    </div>
  );
}

export function Tag({ children, color }: { children: ReactNode; color?: 'cinnabar' | 'bamboo' | 'gold' | 'ink' | 'outline' }) {
  return <span className={`tag ${color ? `tag-${color}` : ''}`}>{children}</span>;
}

export function Status({ type, children }: { type: 'pending' | 'progress' | 'done' | 'warn' | 'open' | 'closed'; children: ReactNode }) {
  return <span className={`status status-${type}`}>{children}</span>;
}

export function Progress({ value, color }: { value: number; color?: 'bamboo' | 'gold' | 'ink' }) {
  return (
    <div className="progress">
      <div className={`progress-bar ${color || ''}`} style={{ width: `${value}%` }} />
    </div>
  );
}

export function Loading() {
  return (
    <div className="fade-in">
      <div className="sk-grid">
        <div className="skeleton sk-kpi" />
        <div className="skeleton sk-kpi" />
        <div className="skeleton sk-kpi" />
        <div className="skeleton sk-kpi" />
      </div>
      <div className="grid grid-2" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 24 }}>
        <div className="skeleton sk-card" />
        <div className="skeleton sk-card" />
      </div>
      <div className="skeleton sk-card" />
    </div>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">⚠</div>
      <div className="empty-title">数据加载失败</div>
      <div className="empty-desc">{message}</div>
    </div>
  );
}


export function EmptyState({ title, desc }: { title: string; desc?: string }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">○</div>
      <div className="empty-title">{title}</div>
      {desc && <div className="empty-desc">{desc}</div>}
    </div>
  );
}

export function ScoreCell({ score }: { score: number }) {
  return (
    <div className="score-cell">
      <div className="score-bar">
        <div className="score-fill" style={{ width: `${score * 10}%` }} />
      </div>
      <span className="score-num">{score}</span>
    </div>
  );
}

export function BudgetBar({ name, planned, used, color }: { name: string; planned: number; used: number; color: string }) {
  const pct = Math.round((used / planned) * 100);
  const gradMap: Record<string, string> = {
    cinnabar: 'linear-gradient(90deg, var(--cinnabar), var(--cinnabar-l))',
    bamboo: 'linear-gradient(90deg, var(--bamboo), var(--bamboo-l))',
    gold: 'linear-gradient(90deg, var(--gold), var(--gold-l))',
    ink: 'linear-gradient(90deg, var(--ink-2), var(--ink-3))',
  };
  return (
    <div className="budget-bar">
      <div className="bb-label">{name}</div>
      <div className="bb-track">
        <div className="bb-fill" style={{ width: `${pct}%`, background: gradMap[color] || gradMap.cinnabar }}>
          {pct}%
        </div>
      </div>
      <div className="bb-amount">¥{used.toLocaleString()} / ¥{planned.toLocaleString()}</div>
    </div>
  );
}

export function FunnelRow({ stage, count, pct, fillColor }: { stage: string; count: number; pct: number; fillColor?: string }) {
  const width = Math.max(pct, 8);
  return (
    <div className="funnel-row">
      <span className="fl-label">{stage}</span>
      <div className="fl-track">
        <div className={`fl-fill ${fillColor || ''}`} style={{ width: `${width}%` }}>
          <span className="fl-count">{count}</span>
          {pct !== 100 && pct > 0 ? <span>{pct}%</span> : null}
        </div>
      </div>
      <span className="fl-pct">{pct === 100 ? '100%' : pct > 0 ? `${pct}%` : ''}</span>
    </div>
  );
}



// ===== Cohesive Funnel Chart (modern data-viz) =====
export function FunnelChart({ data }: {
  data: { id: number; stage: string; count: number; pct: number }[]
}) {
  if (!data || data.length === 0) return null;
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const colorClasses = ['', '', 'bamboo', 'gold', 'ink'];

  return (
    <div className="funnel-chart">
      {data.map((f, i) => {
        const barPct = maxCount > 0 ? (f.count / maxCount) * 100 : 0;
        const prevCount = i > 0 ? data[i - 1].count : null;
        const convRate = prevCount && prevCount > 0
          ? Math.round((f.count / prevCount) * 100) : null;

        return (
          <div key={f.id} className="fc-step" style={{ ["--i" as any]: i }}>
            {i > 0 && convRate !== null && (
              <div className="fc-conv">
                <span className="fc-conv-line" />
                <span className="fc-conv-rate">{convRate}%</span>
                <span className="fc-conv-line" />
              </div>
            )}
            <div className="fc-row">
              <div className="fc-info">
                <span className="fc-stage">{f.stage}</span>
              </div>
              <div className="fc-track">
                <div
                  className={"fc-fill " + (colorClasses[i] || "")}
                  style={{ width: Math.max(barPct, 12) + "%" }}
                >
                  <span className="fc-count">{f.count}</span>
                </div>
              </div>
              <div className="fc-pct">{f.pct}%</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== Department Breakdown (derived from needs data) =====
export function DeptBreakdown({ needs }: {
  needs: { dept: string; position: string; count: number; status: string }[]
}) {
  if (!needs || needs.length === 0) return null;
  const deptMap: Record<string, { count: number; total: number }> = {};
  for (const n of needs) {
    const d = n.dept || "N/A";
    if (!deptMap[d]) deptMap[d] = { count: 0, total: 0 };
    deptMap[d].count += 1;
    deptMap[d].total += n.count || 0;
  }
  const sorted = Object.entries(deptMap)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 6);
  const maxTotal = Math.max(...sorted.map(([, v]) => v.total), 1);

  return (
    <div className="dept-breakdown">
      {sorted.map(([dept, v], i) => (
        <div key={dept} className="db-row" style={{ ["--i" as any]: i }}>
          <div className="db-label" title={dept}>{dept.split(".").pop()?.trim() || dept}</div>
          <div className="db-track">
            <div className="db-fill" style={{ width: (v.total / maxTotal) * 100 + "%" }} />
          </div>
          <div className="db-num">
            <span className="db-hc">{v.total}</span>
            <span className="db-items"> / {v.count}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RadarSVG({ axes, values, size = 320 }: { axes: string[]; values: number[]; size?: number }) {
  const center = size / 2;
  const radius = size * 0.35;
  const n = axes.length;
  const angleStep = (Math.PI * 2) / n;

  let lines = '';
  for (let i = 1; i <= 4; i++) {
    const r = (radius * i) / 4;
    const points: string[] = [];
    for (let j = 0; j < n; j++) {
      const angle = angleStep * j - Math.PI / 2;
      points.push(`${center + Math.cos(angle) * r},${center + Math.sin(angle) * r}`);
    }
    lines += `<polygon points="${points.join(' ')}" fill="none" stroke="var(--paper-deep)" stroke-width="1"/>`;
  }

  let axisLines = '';
  let labels = '';
  for (let i = 0; i < n; i++) {
    const angle = angleStep * i - Math.PI / 2;
    const x = center + Math.cos(angle) * radius;
    const y = center + Math.sin(angle) * radius;
    axisLines += `<line x1="${center}" y1="${center}" x2="${x}" y2="${y}" stroke="var(--paper-deep)" stroke-width="1"/>`;
    const lx = center + Math.cos(angle) * (radius + 22);
    const ly = center + Math.sin(angle) * (radius + 22);
    labels += `<text x="${lx}" y="${ly}" text-anchor="middle" font-size="11" fill="var(--ink-2)" font-family="Noto Serif SC">${axes[i]}</text>`;
  }

  const dataPoints = values.map((v, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const r = (radius * v) / 100;
    return `${center + Math.cos(angle) * r},${center + Math.sin(angle) * r}`;
  }).join(' ');

  const dots = values.map((v, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const r = (radius * v) / 100;
    return `<circle cx="${center + Math.cos(angle) * r}" cy="${center + Math.sin(angle) * r}" r="3" fill="var(--cinnabar)"/>`;
  }).join('');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <g dangerouslySetInnerHTML={{ __html: lines + axisLines }} />
      <polygon points={dataPoints} fill="rgba(200,36,46,0.15)" stroke="var(--cinnabar)" strokeWidth={2} />
      <g dangerouslySetInnerHTML={{ __html: dots }} />
      <g dangerouslySetInnerHTML={{ __html: labels }} />
    </svg>
  );
}


// ===== Scroll Reveal Hook (IntersectionObserver) =====
// Adds 'revealed' class when element enters viewport; cleans up on unmount.
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { el.classList.add('revealed'); return; }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// Wrapper component for scroll-triggered reveal
export function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`scroll-reveal ${className}`}>
      {children}
    </div>
  );
}
